<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
 -->

<template>
  <oxd-dialog class="orangehrm-dialog-modal" @update:show="onCancel">
    <div class="orangehrm-modal-header">
      <oxd-text type="card-title">Leave Workflow History</oxd-text>
    </div>
    <oxd-divider />
    <div v-if="!isLoading" class="orangehrm-modal-content">
      <div v-if="comments.length > 0" class="orangehrm-workflow-history">
        <div
          v-for="(comm, index) in comments"
          :key="comm.id"
          class="orangehrm-workflow-history-item"
        >
          <div class="orangehrm-workflow-history-marker">
            <div
              class="orangehrm-workflow-history-dot"
              :class="{
                'is-first': index === 0,
                'is-last': index === comments.length - 1,
                'is-approved':
                  comm.comment.includes('Approve') ||
                  comm.comment.includes('Accept'),
                'is-rejected': comm.comment.includes('Reject'),
                'is-applied': comm.comment.includes('Applied'),
              }"
            ></div>
            <div
              v-if="index !== comments.length - 1"
              class="orangehrm-workflow-history-line"
            ></div>
          </div>
          <div class="orangehrm-workflow-history-details">
            <div class="orangehrm-workflow-history-action">
              <oxd-text type="subtitle-1">
                {{ formatAction(comm.comment) }}
              </oxd-text>
            </div>
            <div class="orangehrm-workflow-history-meta">
              <oxd-text type="subtitle-2">
                {{ comm.createdByEmployee.firstName }}
                {{ comm.createdByEmployee.lastName || '' }}
              </oxd-text>
              <oxd-text
                type="subtitle-2"
                class="orangehrm-workflow-history-time"
              >
                {{ comm.date }} {{ comm.time || '' }}
              </oxd-text>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="orangehrm-workflow-history-empty">
        <oxd-text type="subtitle-1">
          No workflow history found for this record.
        </oxd-text>
      </div>
    </div>
    <div v-else class="orangehrm-modal-content">
      <oxd-text type="subtitle-1">Loading...</oxd-text>
    </div>
    <oxd-divider />
    <oxd-form-actions>
      <oxd-button
        type="button"
        display-type="ghost"
        label="Close"
        @click="onCancel"
      />
    </oxd-form-actions>
  </oxd-dialog>
</template>

<script>
import {APIService} from '@/core/util/services/api.service';
import {OxdDialog} from '@ohrm/oxd';

export default {
  name: 'LeaveWorkflowHistoryModal',
  components: {
    'oxd-dialog': OxdDialog,
  },
  props: {
    id: {
      type: Number,
      required: true,
    },
    employeeName: {
      type: String,
      default: '',
    },
  },
  emits: ['close'],
  setup(props) {
    const commentService = new APIService(
      window.appGlobal.baseUrl,
      `/api/v2/leave/leave-requests/${props.id}/leave-comments`,
    );
    const requestService = new APIService(
      window.appGlobal.baseUrl,
      `/api/v2/leave/employees/leave-requests/${props.id}`,
    );
    return {
      commentService,
      requestService,
    };
  },
  data() {
    return {
      isLoading: false,
      comments: [],
    };
  },
  async beforeMount() {
    this.isLoading = true;
    try {
      const [commentsResponse, requestResponse] = await Promise.all([
        this.commentService
          .getAll({
            limit: 0,
            sortField: 'leaveRequestComment.createdAt',
            sortOrder: 'ASC',
          })
          .catch(() => {
            return {data: {data: []}};
          }),
        this.requestService.getAll().catch(() => {
          return null;
        }),
      ]);

      const data =
        (commentsResponse &&
          commentsResponse.data &&
          commentsResponse.data.data) ||
        [];

      // Filter only system logs
      let systemComments = data.filter(
        (c) =>
          c.comment &&
          (c.comment.startsWith('[System]') ||
            c.comment.match(
              /^(Approve|Reject|Applied|Assign|Cancel|.* changed) by/i,
            )),
      );

      // If "Applied" is missing, synthesize it
      const hasApplied = systemComments.some((c) =>
        c.comment.toLowerCase().includes('applied'),
      );

      if (!hasApplied) {
        const firstName = this.employeeName
          ? this.employeeName.split(' ')[0]
          : 'Applicant';
        const lastName = this.employeeName
          ? this.employeeName.split(' ').slice(1).join(' ')
          : '';

        let applyDate = '';
        if (
          requestResponse &&
          requestResponse.data &&
          requestResponse.data.data
        ) {
          applyDate = requestResponse.data.data.dateApplied || '';
        }

        systemComments.unshift({
          id: 'synthetic-applied',
          comment: `[System] Applied by ${this.employeeName || 'Applicant'}`,
          createdByEmployee: {
            firstName,
            lastName,
          },
          date: applyDate,
          time: '',
        });
      }

      this.comments = systemComments;
    } catch (e) {
      // Ignore
    } finally {
      this.isLoading = false;
    }
  },
  methods: {
    onCancel() {
      this.$emit('close', true);
    },
    formatAction(comment) {
      let action = comment.replace('[System] ', '').split(' by ')[0];
      // Clean up common roles/actions
      if (action === 'Accept Cover') return 'Cover Accepted';
      if (action === 'Reject Cover') return 'Cover Rejected';
      return action;
    },
  },
};
</script>

<style lang="scss" scoped>
.orangehrm-workflow-history {
  padding: 20px;

  &-empty {
    padding: 20px;
    text-align: center;
    color: var(--oxd-text-light-color);
  }

  &-item {
    display: flex;
    gap: 20px;
    margin-bottom: 20px;
  }

  &-marker {
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
    padding-top: 5px;
  }

  &-dot {
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background-color: var(--oxd-primary-one-color);
    z-index: 1;

    &.is-applied {
      background-color: #3498db; // Blue for start
    }

    &.is-approved {
      background-color: #2ecc71; // Green for approval
    }

    &.is-rejected {
      background-color: #e74c3c; // Red for rejection
    }

    &.is-last:not(.is-rejected) {
      box-shadow: 0 0 0 3px rgba(46, 204, 113, 0.2);
    }
  }

  &-line {
    width: 2px;
    flex: 1;
    background-color: var(--oxd-border-color);
    margin: 4px 0;
  }

  &-details {
    flex: 1;
    background: #f9f9f9;
    padding: 10px 15px;
    border-radius: 8px;
    border-left: 3px solid var(--oxd-primary-one-color);
  }

  &-action {
    margin-bottom: 4px;
    font-weight: bold;
    color: var(--oxd-text-main-color);
  }

  &-meta {
    display: flex;
    justify-content: space-between;
    color: var(--oxd-text-light-color);
    font-size: 0.85em;
  }

  &-time {
    font-style: italic;
  }
}
</style>
